import React, { useState, useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import { Input } from "./ui/input";
import { Briefcase, MapPin, Calendar, Trash2, Loader2, Search, X, Pencil, RotateCcw, Users } from "lucide-react";
import DocCountBadge from "./DocCountBadge";

function formatDate(iso) {
    if (!iso) return "";
    // Accept both "YYYY-MM-DD" and full ISO timestamps.
    const datePart = String(iso).slice(0, 10);
    const [y, m, d] = datePart.split("-");
    if (!y || !m || !d || y.length !== 4) return iso;
    return `${m}/${d}/${y}`;
}

export const AllProjectsDialog = ({ open, onOpenChange, projects = [], onDelete, onReset, onEdit, onShareToWorkgroup, uploadCounts = {}, onSelect }) => {
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [query, setQuery] = useState("");

    const filteredProjects = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return projects;
        return projects.filter((p) => {
            const name = (p.name || "").toLowerCase();
            const jobId = (p.job_id || "").toLowerCase();
            const location = (p.location || "").toLowerCase();
            return name.includes(q) || jobId.includes(q) || location.includes(q);
        });
    }, [projects, query]);

    const confirmDelete = async () => {
        if (!pendingDelete || !onDelete) return;
        setDeleting(true);
        try {
            await onDelete(pendingDelete);
            setPendingDelete(null);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[640px] max-h-[85vh] overflow-hidden p-4 sm:p-6"
                    data-testid="all-projects-dialog"
                >
                    <div className="min-w-0 flex flex-col gap-4 max-h-[calc(85vh-2rem)]">
                        <DialogHeader className="min-w-0">
                            <DialogTitle className="text-white flex items-center gap-2">
                                All Projects
                                <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                                    {query ? `${filteredProjects.length} / ${projects.length}` : `${projects.length} total`}
                                </span>
                            </DialogTitle>
                            <DialogDescription className="text-slate-400">
                                Every project saved to your Steeltooth workspace.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="relative min-w-0 shrink-0">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                            <Input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by name/address/id"
                                data-testid="input-projects-search"
                                className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF] pl-9 pr-9 h-10"
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => setQuery("")}
                                    data-testid="btn-clear-projects-search"
                                    aria-label="Clear search"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-sm flex items-center justify-center text-slate-400 hover:text-white hover:bg-[#1C253C] transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        <ul
                            className="min-w-0 flex-1 overflow-y-auto space-y-2 pr-1"
                            data-testid="all-projects-list"
                        >
                            {projects.length === 0 && (
                                <li className="text-sm text-slate-400 p-4 text-center">
                                    No projects yet. Create your first project from the home screen.
                                </li>
                            )}
                            {projects.length > 0 && filteredProjects.length === 0 && (
                                <li
                                    className="text-sm text-slate-400 p-6 text-center"
                                    data-testid="projects-search-empty"
                                >
                                    No projects match{" "}
                                    <span className="text-white font-semibold">
                                        &ldquo;{query}&rdquo;
                                    </span>
                                    .
                                </li>
                            )}
                            {filteredProjects.map((p) => (
                                <li
                                    key={p.id}
                                    data-testid={`all-projects-item-${p.id}`}
                                    className="flex flex-col sm:flex-row sm:items-center gap-3 px-3 py-3 bg-[#0A1128] border border-[#2A3655] rounded-md min-w-0 hover:border-[#00A3FF]/60 transition-colors cursor-pointer"
                                    onClick={() => onSelect && onSelect(p)}
                                >
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div className="w-9 h-9 rounded-sm bg-[#1C253C] border border-[#2A3655] text-[#00A3FF] flex items-center justify-center shrink-0">
                                        <Briefcase className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                            <div
                                                className="font-semibold text-white text-sm overflow-hidden text-ellipsis whitespace-nowrap"
                                                title={p.name}
                                            >
                                                {p.name}
                                            </div>
                                            {p.job_id && (
                                                <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#007AFF]/15 border border-[#007AFF]/40 text-[#00A3FF]">
                                                    {p.job_id}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400 mt-0.5">
                                            <span className="flex items-center gap-1 min-w-0">
                                                <MapPin className="w-3 h-3 shrink-0" />
                                                <span className="truncate">{p.location}</span>
                                            </span>
                                            <span className="flex items-center gap-1 shrink-0">
                                                <Calendar className="w-3 h-3" />
                                                {formatDate(p.updated_at)}
                                            </span>
                                        </div>
                                    </div>
                                    </div>
                                    <div
                                        className="flex items-center gap-1 justify-end sm:shrink-0 sm:ml-auto"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                    <DocCountBadge
                                        count={uploadCounts[p.id] || 0}
                                        testId={`all-projects-doc-count-${p.id}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit && onEdit(p);
                                        }}
                                        data-testid={`btn-edit-project-${p.id}`}
                                        aria-label={`Edit ${p.name}`}
                                        className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-[#00A3FF] hover:bg-[#007AFF]/10 border border-transparent hover:border-[#00A3FF]/30 transition-colors active:scale-95"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    {onShareToWorkgroup && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onShareToWorkgroup(p);
                                            }}
                                            data-testid={`btn-share-wg-project-${p.id}`}
                                            aria-label={`Share ${p.name} to workgroup`}
                                            title="Share to workgroup — send this project (plans, analyses, estimates, takeoffs) to a shared team workspace"
                                            className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 border border-transparent hover:border-emerald-500/30 transition-colors active:scale-95"
                                        >
                                            <Users className="w-4 h-4" />
                                        </button>
                                    )}
                                    {onReset && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onReset(p);
                                            }}
                                            data-testid={`btn-reset-project-${p.id}`}
                                            aria-label={`Reset ${p.name} — wipe takeoff drawings`}
                                            title="Reset takeoffs — wipe all AI-drawn shapes. Uploads, page splits, analyses, and estimates are preserved."
                                            className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 border border-transparent hover:border-amber-500/30 transition-colors active:scale-95"
                                        >
                                            <RotateCcw className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPendingDelete(p);
                                        }}
                                        data-testid={`btn-delete-project-${p.id}`}
                                        aria-label={`Delete ${p.name}`}
                                        className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-colors active:scale-95"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}
            >
                <AlertDialogContent
                    className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[440px]"
                    data-testid="delete-project-confirm"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Delete this project?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            <span className="text-white font-semibold">
                                {pendingDelete?.name}
                            </span>
                            {pendingDelete?.job_id ? (
                                <>
                                    {" "}
                                    (
                                    <span className="font-mono text-[#00A3FF]">
                                        {pendingDelete.job_id}
                                    </span>
                                    )
                                </>
                            ) : null}
                            {" "}will be permanently removed from your workspace.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="btn-cancel-delete-project"
                            className="bg-[#1C253C] border-[#2A3655] text-slate-300 hover:bg-[#2A3655] hover:text-white"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            data-testid="btn-confirm-delete-project"
                            className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default AllProjectsDialog;
